import Head from "next/head";
import { memo } from "react";
import { useTranslation } from "react-i18next";

interface PageHeadProps {
  namespace: string;
  titleKey?: string;
  descriptionKey?: string;
  titleValues?: Record<string, string | number>;
  descriptionValues?: Record<string, string | number>;
  imageUrl?: string;
}

function PageHead({
  namespace,
  titleKey = "meta.title",
  descriptionKey = "meta.description",
  titleValues,
  descriptionValues,
  imageUrl = "/lazo-og-image.svg",
}: PageHeadProps) {
  const { t } = useTranslation(namespace);

  const title = t(titleKey, titleValues);
  const description = t(descriptionKey, descriptionValues);
  const siteName = t("common:siteName");

  const fullTitle = title && title !== siteName ? `${title} - Lazo` : siteName;
  const displayTitle = fullTitle === "siteName" ? "Lazo" : fullTitle;

  // Get the current URL (will be set dynamically on client side)
  const currentUrl = typeof window !== "undefined" ? window.location.href : "https://lazo.one";

  // Construct full image URL
  const fullImageUrl = imageUrl.startsWith("http")
    ? imageUrl
    : `${typeof window !== "undefined" ? window.location.origin : "https://lazo.one"}${imageUrl}`;

  return (
    <Head>
      {/* Basic meta tags */}
      <title>{displayTitle}</title>
      <meta name="description" content={description} />

      {/* Open Graph meta tags for Facebook, WhatsApp, LinkedIn */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={displayTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:url" content={currentUrl} />

      {/* Twitter Card meta tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={displayTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
    </Head>
  );
}

export default memo(PageHead);
